import React from 'react';
import { useQuery } from 'react-query';
import { motion } from 'framer-motion';
import { FiFileText, FiUsers, FiFolder, FiDatabase, FiCloud, FiRefreshCw, FiAlertCircle } from 'react-icons/fi';
import '../../styles/main.scss';
import * as documentService from '../../services/documents';
import * as categoryService from '../../services/categories';
import * as userService from '../../services/users';
import api from '../../services/api';

const fetchDriveUsage = async () => {
  const response = await api.get('/drive/usage');
  return response.data;
};

const Reports = () => {
  const {
    data: documentStatsResponse,
    isLoading: isDocumentsLoading,
    isError: isDocumentsError,
    refetch: refetchDocumentStats,
  } = useQuery('reports-document-stats', () => documentService.getDocumentStats());

  const {
    data: categoryStatsResponse,
    isLoading: isCategoriesLoading,
    isError: isCategoriesError,
    refetch: refetchCategoryStats,
  } = useQuery('reports-category-stats', () => categoryService.getCategoryStats());

  const {
    data: userStatsResponse,
    isLoading: isUsersLoading,
    isError: isUsersError,
    refetch: refetchUserStats,
  } = useQuery('reports-user-stats', () => userService.getUserStats());

  const {
    data: driveUsageResponse,
    isLoading: isDriveLoading,
    isError: isDriveError,
    refetch: refetchDriveUsage,
  } = useQuery('reports-drive-usage', fetchDriveUsage);

  const documentStats = documentStatsResponse?.data || {};
  const categoryStats = categoryStatsResponse?.data || {};
  const userStats = userStatsResponse?.stats || {};
  const driveStats = driveUsageResponse?.data || {};

  const overallDocuments = documentStats.overall || {};
  const documentsByStatus = documentStats.byStatus || [];
  const monthlyTrend = documentStats.monthlyTrend || [];

  const totalDocuments = overallDocuments.total || 0;
  const pendingDocuments = overallDocuments.pending || 0;
  const approvedDocuments = overallDocuments.approved || 0;
  const urgentDocuments = overallDocuments.urgent || 0;

  const totalCategories = categoryStats.totalCategories || 0;
  const categoriesByLevel = categoryStats.byLevel || [];
  const mostUsedCategories = categoryStats.mostUsed || [];

  const totalUsers = userStats.totalUsers || 0;
  const activeUsers = userStats.activeUsers || 0;
  const usersByRole = userStats.byRole || [];

  const googleDriveStats = driveStats.googleDrive || {};
  const databaseStats = driveStats.database || {};

  const storageUsedMb = databaseStats.totalSizeMB || 0;
  const totalDriveFiles = googleDriveStats.totalFiles || 0;

  const isLoading =
    isDocumentsLoading || isCategoriesLoading || isUsersLoading || isDriveLoading;

  const handleRefresh = () => {
    refetchDocumentStats();
    refetchCategoryStats();
    refetchUserStats();
    refetchDriveUsage();
  };

  const formatMonthLabel = (item) => {
    if (!item || !item._id) {
      return '-';
    }
    const year = item._id.year;
    const month = item._id.month;
    const paddedMonth = month.toString().padStart(2, '0');
    return `${year}-${paddedMonth}`;
  };

  const formatBytes = (bytes) => {
    if (!bytes || Number.isNaN(Number(bytes))) {
      return '0 MB';
    }
    const megabytes = Number(bytes) / (1024 * 1024);
    if (megabytes < 1024) {
      return `${megabytes.toFixed(2)} MB`;
    }
    const gigabytes = megabytes / 1024;
    return `${gigabytes.toFixed(2)} GB`;
  };

  return (
    <div className="dashboard-page reports-page">
      <div className="page-header">
        <div>
          <h1>Reports</h1>
          <p>Overview of documents, categories, users and storage</p>
        </div>
        <button
          type="button"
          className="btn btn-outline"
          onClick={handleRefresh}
          disabled={isLoading}
        >
          <FiRefreshCw />
          Refresh
        </button>
      </div>

      <div className="page-content">
        <div className="stats-grid">
          <motion.div
            className="stat-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="stat-link">
              <div
                className="stat-icon"
                style={{ backgroundColor: '#0f6c1115', color: '#0f6c11' }}
              >
                <FiFileText />
              </div>
              <div className="stat-info">
                <h3>{totalDocuments}</h3>
                <p>Total documents</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="stat-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <div className="stat-link">
              <div
                className="stat-icon"
                style={{ backgroundColor: '#1a9b1d15', color: '#1a9b1d' }}
              >
                <FiUsers />
              </div>
              <div className="stat-info">
                <h3>
                  {activeUsers}/{totalUsers}
                </h3>
                <p>Active users</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="stat-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="stat-link">
              <div
                className="stat-icon"
                style={{ backgroundColor: '#2bc93015', color: '#2bc930' }}
              >
                <FiFolder />
              </div>
              <div className="stat-info">
                <h3>{totalCategories}</h3>
                <p>Active categories</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="stat-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <div className="stat-link">
              <div
                className="stat-icon"
                style={{ backgroundColor: '#3B82F615', color: '#3B82F6' }}
              >
                <FiDatabase />
              </div>
              <div className="stat-info">
                <h3>{formatBytes(databaseStats.totalSize)}</h3>
                <p>Document storage used</p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="dashboard-sections">
          <motion.div
            className="section-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="section-header">
              <h2>Document status overview</h2>
            </div>
            <div className="section-content">
              {isDocumentsLoading ? (
                <div className="loading-placeholder">
                  {[...Array(3)].map((_, index) => (
                    <div
                      key={index}
                      className="skeleton skeleton-text"
                      style={{ height: '32px' }}
                    />
                  ))}
                </div>
              ) : isDocumentsError ? (
                <div className="error-state">
                  <FiAlertCircle />
                  <p>Failed to load document statistics.</p>
                </div>
              ) : totalDocuments === 0 ? (
                <div className="empty-state">
                  <FiFileText className="empty-icon" />
                  <p>No documents available yet.</p>
                </div>
              ) : (
                <div className="status-grid">
                  <div className="status-summary">
                    <div className="status-item">
                      <span className="status-label">Pending</span>
                      <span className="status-value">{pendingDocuments}</span>
                    </div>
                    <div className="status-item">
                      <span className="status-label">Approved</span>
                      <span className="status-value">{approvedDocuments}</span>
                    </div>
                    <div className="status-item">
                      <span className="status-label">Urgent priority</span>
                      <span className="status-value">{urgentDocuments}</span>
                    </div>
                  </div>
                  <div className="status-breakdown">
                    {documentsByStatus.map((item) => (
                      <div key={item._id} className="status-row">
                        <span className="status-label">{item._id}</span>
                        <span className="status-count">{item.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          <motion.div
            className="section-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
          >
            <div className="section-header">
              <h2>Monthly document trend</h2>
            </div>
            <div className="section-content">
              {isDocumentsLoading ? (
                <div className="loading-placeholder">
                  {[...Array(4)].map((_, index) => (
                    <div
                      key={index}
                      className="skeleton skeleton-text"
                      style={{ height: '24px' }}
                    />
                  ))}
                </div>
              ) : isDocumentsError ? (
                <div className="error-state">
                  <FiAlertCircle />
                  <p>Failed to load trend data.</p>
                </div>
              ) : monthlyTrend.length === 0 ? (
                <div className="empty-state">
                  <FiTrendingUp className="empty-icon" />
                  <p>No trend data available.</p>
                </div>
              ) : (
                <div className="trend-list">
                  {monthlyTrend.map((item) => (
                    <div key={formatMonthLabel(item)} className="trend-row">
                      <span className="trend-label">{formatMonthLabel(item)}</span>
                      <span className="trend-value">{item.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>

          <motion.div
            className="section-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="section-header">
              <h2>Category distribution</h2>
            </div>
            <div className="section-content">
              {isCategoriesLoading ? (
                <div className="loading-placeholder">
                  {[...Array(3)].map((_, index) => (
                    <div
                      key={index}
                      className="skeleton skeleton-text"
                      style={{ height: '32px' }}
                    />
                  ))}
                </div>
              ) : isCategoriesError ? (
                <div className="error-state">
                  <FiAlertCircle />
                  <p>Failed to load category statistics.</p>
                </div>
              ) : totalCategories === 0 ? (
                <div className="empty-state">
                  <FiFolder className="empty-icon" />
                  <p>No categories configured.</p>
                </div>
              ) : (
                <div className="category-grid">
                  <div className="category-levels">
                    <h3>Categories by level</h3>
                    {categoriesByLevel.length === 0 ? (
                      <p className="text-sm">No level data available.</p>
                    ) : (
                      categoriesByLevel.map((item) => (
                        <div key={item._id} className="category-row">
                          <span className="category-label">Level {item._id}</span>
                          <span className="category-count">{item.count}</span>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="category-most-used">
                    <h3>Most used categories</h3>
                    {mostUsedCategories.length === 0 ? (
                      <p className="text-sm">No usage data available.</p>
                    ) : (
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Category</th>
                            <th>Documents</th>
                            <th>Total size</th>
                          </tr>
                        </thead>
                        <tbody>
                          {mostUsedCategories.map((item) => (
                            <tr key={item.categoryId}>
                              <td>{item.categoryName}</td>
                              <td>{item.documentCount}</td>
                              <td>{formatBytes(item.totalSize)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          <motion.div
            className="section-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
          >
            <div className="section-header">
              <h2>User breakdown</h2>
            </div>
            <div className="section-content">
              {isUsersLoading ? (
                <div className="loading-placeholder">
                  {[...Array(3)].map((_, index) => (
                    <div
                      key={index}
                      className="skeleton skeleton-text"
                      style={{ height: '32px' }}
                    />
                  ))}
                </div>
              ) : isUsersError ? (
                <div className="error-state">
                  <FiAlertCircle />
                  <p>Failed to load user statistics.</p>
                </div>
              ) : totalUsers === 0 ? (
                <div className="empty-state">
                  <FiUsers className="empty-icon" />
                  <p>No users found.</p>
                </div>
              ) : (
                <div className="user-roles">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Role</th>
                        <th>Users</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usersByRole.map((item) => (
                        <tr key={item._id}>
                          <td>{item._id}</td>
                          <td>{item.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </motion.div>

          <motion.div
            className="section-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="section-header">
              <h2>Storage overview</h2>
            </div>
            <div className="section-content">
              {isDriveLoading ? (
                <div className="loading-placeholder">
                  {[...Array(2)].map((_, index) => (
                    <div
                      key={index}
                      className="skeleton skeleton-text"
                      style={{ height: '32px' }}
                    />
                  ))}
                </div>
              ) : isDriveError ? (
                <div className="error-state">
                  <FiAlertCircle />
                  <p>Failed to load storage statistics.</p>
                </div>
              ) : (
                <div className="storage-grid">
                  <div className="storage-card">
                    <div className="storage-header">
                      <FiCloud />
                      <div>
                        <h3>Google Drive</h3>
                        <p className="text-sm text-muted">
                          Synced files in connected drive
                        </p>
                      </div>
                    </div>
                    <div className="storage-body">
                      <div className="storage-metric">
                        <span className="label">Total files</span>
                        <span className="value">{totalDriveFiles}</span>
                      </div>
                      <div className="storage-metric">
                        <span className="label">Total size</span>
                        <span className="value">
                          {Number(googleDriveStats.totalSizeMB || 0).toFixed(2)} MB
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="storage-card">
                    <div className="storage-header">
                      <FiDatabase />
                      <div>
                        <h3>Database</h3>
                        <p className="text-sm text-muted">
                          Documents stored in the system
                        </p>
                      </div>
                    </div>
                    <div className="storage-body">
                      <div className="storage-metric">
                        <span className="label">Total documents</span>
                        <span className="value">
                          {databaseStats.totalDocuments || 0}
                        </span>
                      </div>
                      <div className="storage-metric">
                        <span className="label">Total size</span>
                        <span className="value">
                          {Number(storageUsedMb).toFixed(2)} MB
                        </span>
                      </div>
                      <div className="storage-metric">
                        <span className="label">Average document size</span>
                        <span className="value">
                          {formatBytes(databaseStats.avgDocumentSize)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
